import type { FocusResponse } from "@starsach/shared-types";
import { Link, useLocation } from "react-router-dom";
import { useFocus } from "../api/hooks";
import { useAuth } from "../auth/AuthProvider";
import { Hint } from "../components/Hint";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, PageHeader, RelativeTime, StatTile, StatusPill } from "../components/ui";
import { QUOTA_REQUEST_STATUS } from "../lib/quota-requests";
import { useFormat } from "../lib/useFormat";

const pct = (n: number, d: number) => (d ? `${((n / d) * 100).toFixed(1)}%` : "-");

type PendingApproval = NonNullable<FocusResponse["pendingApprovals"]>[number];

/** The words the Promotions page uses for the same request (ADR-0008). */
function describeChange(p: PendingApproval): string {
  switch (p.kind) {
    case "promotion": return `Promotion from ${p.sourceFriendlyName ?? p.sourceEnvironmentId} to ${p.friendlyName}`;
    case "rollback": return `Rollback in ${p.friendlyName}`;
    default: return `Direct edit in ${p.friendlyName}`;
  }
}

export function FocusPage() {
  const fmt = useFormat();
  const { me } = useAuth();
  const focus = useFocus();
  const fallback = (useLocation().state as { landingFallback?: string } | null)?.landingFallback;
  if (focus.isPending) return <LoadingState label="Loading what needs attention" />;
  if (focus.isError) return <ErrorState error={focus.error} onRetry={() => void focus.refetch()} />;
  const f = focus.data;
  const internal = me?.role === "internal-team";

  return (
    <>
      <PageHeader title="Focus" subtitle={`What needs attention today · generated ${fmt.time(f.generatedAt)}`} />
      {fallback && (
        <p role="status" className="mb-4 rounded border border-neutral-200 bg-neutral-50 p-3 text-sm dark:border-neutral-800 dark:bg-neutral-900">
          Your chosen start page ({fallback}) is no longer available to you, so you are on Focus. <Link to="/account" className="underline">Pick another</Link>.
        </p>
      )}
      <div className={`grid gap-4 ${internal ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"}`}>
        <StatTile label="Critical alarms open" value={f.tiles.criticalAlarmsOpen} tone={f.tiles.criticalAlarmsOpen ? "critical" : "ok"} hint={<Hint id="focus.critical-alarms" label="critical alarms" />} />
        <StatTile label="Quotas at 80%+" value={f.tiles.quotasNearLimit} tone={f.tiles.quotasNearLimit ? "warning" : "ok"} hint={<Hint id="focus.quotas-near-limit" label="quotas near their limit" />} />
        {internal && <StatTile label="Accounts with no heartbeat" value={f.tiles.accountsGoneQuiet ?? 0} tone={f.tiles.accountsGoneQuiet ? "warning" : "ok"} to="/customers" hint={<Hint id="focus.accounts-no-heartbeat" label="accounts with no heartbeat" />} />}
        <StatTile label="Open support cases" value={f.tiles.openSupportCases} hint={<Hint id="focus.open-support-cases" label="open support cases" />} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {/* #322: only for someone who can approve somewhere (the API leaves it out otherwise); first, because only they can move it. */}
        {f.pendingApprovals && (
          <Card title={<>Waiting for your approval<Hint id="focus.pending-approvals" label="changes waiting for your approval" /></>} className="lg:col-span-2">
            <DataTable
              caption="Configuration changes waiting for your approval, newest first"
              rows={f.pendingApprovals}
              rowKey={(p) => `${p.environmentId}:${p.promotionId}`}
              empty={<EmptyState title="Nothing is waiting for your approval" hint="A promotion, a gated edit or a rollback into an environment you approve for lands here." />}
              columns={[
                {
                  key: "what",
                  header: "Change",
                  render: (p) => (
                    <Link className="font-medium text-accent-strong underline" to={`/flow-configuration/${p.environmentId}/promotions`}>
                      {describeChange(p)}
                    </Link>
                  ),
                },
                { key: "profile", header: "Profile", render: (p) => p.profileName ?? p.profileId },
                { key: "environment", header: "Environment", render: (p) => p.friendlyName },
                { key: "by", header: "Requested by", render: (p) => p.requestedBy },
                { key: "when", header: "Requested", render: (p) => <RelativeTime iso={p.requestedAt} /> },
              ]}
            />
          </Card>
        )}

        <Card title="Critical alarms">
          <DataTable
            caption="Open critical alarms"
            rows={f.criticalAlarms}
            rowKey={(a) => `${a.environmentId}:${a.alarmArn}`}

            sortBy={(a) => a.alarmName}
            empty={<EmptyState title="No open critical alarms" hint="Nothing critical is firing right now." />}
            columns={[
              { key: "alarm", header: "Alarm", render: (a) => <Link className="font-medium text-accent-strong underline" to={`/alarms/${a.environmentId}`}>{a.alarmName}</Link> },
              ...(internal ? [{ key: "customer", header: "Customer", render: (a: (typeof f.criticalAlarms)[number]) => <Link className="underline" to={`/customers/${a.environmentId}`}>{a.friendlyName}</Link> }] : []),
              { key: "seen", header: "Last seen", render: (a) => <RelativeTime iso={a.lastSeenAt} /> },
              { key: "count", header: "Occurrences", numeric: true, render: (a) => a.occurrenceCount },
              { key: "ticket", header: "Ticket", render: (a) => (a.hasTicket ? "open" : "none") },
            ]}
          />
        </Card>

        <Card title="Quotas nearing breach">
          <DataTable
            caption="Quotas at or above 80 percent of their limit"
            rows={f.quotasNearLimit}
            rowKey={(q) => `${q.environmentId}:${q.serviceCode}:${q.quotaCode}`}

            sortBy={(q) => -q.usagePercent}
            empty={<EmptyState title="No quotas near their limit" />}
            columns={[
              { key: "quota", header: "Quota", render: (q) => <Link className="font-medium text-accent-strong underline" to={`/quotas/${q.environmentId}`}>{q.quotaName}</Link> },
              ...(internal ? [{ key: "customer", header: "Customer", render: (q: (typeof f.quotasNearLimit)[number]) => q.friendlyName }] : []),
              { key: "usage", header: "Usage", numeric: true, render: (q) => `${q.usage} / ${q.value}` },
              { key: "pct", header: "%", numeric: true, render: (q) => `${q.usagePercent.toFixed(0)}%` },
              {
                key: "action",
                header: "",
                render: (q) =>
                  q.openRequestStatus ? (
                    // Already with AWS: say so rather than offer a second request, which the API would refuse anyway.
                    <Link to={`/quotas/${q.environmentId}`} className="inline-flex items-center gap-1.5 whitespace-nowrap" title="An increase request is already open for this quota">
                      <StatusPill status={QUOTA_REQUEST_STATUS[q.openRequestStatus].label} tone={QUOTA_REQUEST_STATUS[q.openRequestStatus].tone} />
                    </Link>
                  ) : q.increaseRequestsAvailable && q.adjustable ? (
                    <Link className="underline" to={`/quotas/${q.environmentId}`}>Request increase</Link>
                  ) : null,
              },
            ]}
          />
        </Card>

        {internal && f.accountsGoneQuiet && (
          <Card title="Accounts with no heartbeat" className="lg:col-span-2">
            <DataTable
              caption="Customers whose stack has stopped reporting"
              rows={f.accountsGoneQuiet}
              rowKey={(c) => c.environmentId}

              sortBy={(c) => c.friendlyName}
              empty={<EmptyState title="Every active account is reporting" />}
              columns={[
                { key: "customer", header: "Customer", render: (c) => <Link className="font-medium text-accent-strong underline" to={`/customers/${c.environmentId}`}>{c.friendlyName}</Link> },
                { key: "reason", header: "Why", render: (c) => ({ never_connected: "Never completed the connectivity test", no_heartbeat: "Connected but never heartbeat", heartbeat_stale: "Heartbeat stopped" })[c.reason] },
                { key: "hb", header: "Last heartbeat", render: (c) => <RelativeTime iso={c.lastHeartbeatAt} /> },
              ]}
            />
          </Card>
        )}
      </div>

      {/* Section 8.9: a row from Journeys, so Focus says how the day went and not only what is on fire. Below the worklists: what needs doing comes before how yesterday went (the owner's rule of 2026-09-21). */}
      {f.yesterday && (
        <Card title={`Yesterday, across ${f.yesterday.environments === 1 ? "your environment" : `${f.yesterday.environments} environments`}`} className="mt-6">
          <dl className="grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <dt className="text-xs text-neutral-500">Contacts</dt>
              <dd className="mt-1 text-2xl font-semibold tabular-nums"><Link to="/journeys" className="hover:underline">{f.yesterday.contacts.toLocaleString()}</Link></dd>
            </div>
            <div>
              <dt className="text-xs text-neutral-500">Contained by IVR/bot<Hint id="journeys.contained" label="contained" /></dt>
              <dd className="mt-1 text-2xl font-semibold tabular-nums"><Link to="/journeys" className="hover:underline">{pct(f.yesterday.contained, f.yesterday.contacts)}</Link></dd>
            </div>
            <div>
              <dt className="text-xs text-neutral-500">Abandoned in queue<Hint id="journeys.abandoned" label="abandoned" /></dt>
              <dd className={`mt-1 text-2xl font-semibold tabular-nums ${f.yesterday.contacts && f.yesterday.abandoned / f.yesterday.contacts > 0.05 ? "text-amber-700 dark:text-amber-300" : ""}`}><Link to="/journeys" className="hover:underline">{pct(f.yesterday.abandoned, f.yesterday.contacts)}</Link></dd>
            </div>
            <div>
              <dt className="text-xs text-neutral-500">Poor audio (either side)<Hint id="journeys.poor-audio" label="poor audio" /></dt>
              <dd className="mt-1 text-2xl font-semibold tabular-nums"><Link to="/journeys" className="hover:underline">{pct(f.yesterday.poorAudio, f.yesterday.contacts)}</Link></dd>
            </div>
            <div>
              <dt className="text-xs text-neutral-500">Config changes</dt>
              <dd className="mt-1 text-2xl font-semibold tabular-nums"><Link to="/audit" className="hover:underline">{f.yesterday.configChanges.toLocaleString()}</Link></dd>
            </div>
          </dl>
          <p className="mt-3 text-xs text-neutral-500">{f.yesterday.date} (UTC) · each number opens the view it came from<Hint id="focus.yesterday" label="this row" /></p>
        </Card>
      )}
    </>
  );
}
